const VOID_TAGS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'source', 'track', 'wbr'])

const TAG_PATTERN = /<(\/?)([a-z][a-z0-9-]*)(?:\s(?:[^>"']|"[^"]*"|'[^']*')*)?\/?>/gi

/**
 * Balances the tags of an HTML fragment so it is safe to SSR through `v-html`.
 *
 * `v-html` markup is emitted verbatim into the server-rendered document. When a
 * fragment leaves a formatting tag unclosed (e.g. a CMS-entered `<b>` with no
 * `</b>`), the browser's parser re-opens that tag *after* the fragment's
 * container closes and wraps the following sibling elements in reconstructed
 * tags. Hydration then walks a DOM that doesn't match the vdom, and Vue's
 * mismatch recovery can re-create the surrounding blocks in the wrong order
 * while leaving stale, never-mounted SSR markup behind.
 *
 * Pure string processing (no DOMParser), so server and client produce the
 * identical fragment. This is a tag balancer, not a sanitizer: unmatched
 * closing tags are dropped and unclosed tags are closed, innermost first, at
 * the end of the fragment.
 */
export default function balanceHtml(html: string | null | undefined): string {
    if (!html) return ''

    let out = ''
    let last = 0
    const open: string[] = []

    for (const match of html.matchAll(TAG_PATTERN)) {
        const [raw, slash, rawName] = match
        const name = rawName!.toLowerCase()

        out += html.slice(last, match.index)
        last = match.index + raw.length

        if (VOID_TAGS.has(name)) {
            out += raw
        }
        else if (!slash && raw.endsWith('/>')) {
            // Browsers ignore the self-closing flag on non-void elements, so
            // `<b/>` would stay open. Make it an explicit empty element.
            out += `${raw.slice(0, -2).trimEnd()}></${name}>`
        }
        else if (!slash) {
            out += raw
            open.push(name)
        }
        else if (open.includes(name)) {
            // Also closes any unclosed tags nested inside the one being closed.
            let popped: string | undefined
            do {
                popped = open.pop()
                out += `</${popped}>`
            } while (popped !== name)
        }
        // Closing tag that was never opened: drop it.
    }

    out += html.slice(last)

    while (open.length) {
        out += `</${open.pop()}>`
    }

    return out
}
